import {cssTheme} from '@vio/atlas/src/themes'

import {MessageConfig, PriceTrendInsightsPayload} from '../types'
import {
  HotelTrendDirection,
  PriceTrendIconKey,
  SimilarHotelsDirection
} from '../types'
import {PriceTrendMessageKey} from '../utils/messages'

/**
 * A/B test: "Our advice" panel (Price Insights v4, mobile web only).
 * Variant renders the verdict + reasons "Our advice" layer; control is
 * today's Price Insights panel, unchanged. Gated on Price Insights eligibility.
 * Generated via `make generate-experiment-id id=price-insights-our-advice`.
 */
export const PRICE_INSIGHTS_OUR_ADVICE_EXPERIMENT =
  'c44047ae-price-insights-our-advice'

/**
 * Configuration object for PriceTrend component
 * Contains all constants, thresholds, defaults, and mappings
 */
export const PRICE_TREND_CONFIG = {
  /**
   * Thresholds for determining trend directions
   * Note: These are fallback values; the API provides thresholds per-request
   */
  thresholds: {
    /**
     * Fallback threshold below which price changes are considered "no change"
     * Value: 0.02 (2%)
     */
    noChange: 0.02
  },
  /**
   * Default values for PriceTrendInsightsPayload
   */
  defaults: {
    insights: {
      medianHotelPrice: 0,
      timeRangeDays: 7,
      lastMedianSimilarHotelsPricePoint: null,
      hotelChangePercentageThreshold: 0.02,
      marketChangePercentageThreshold: 0.1,
      marketPriceTrendPercentage: null
    } as const satisfies PriceTrendInsightsPayload
  }
} as const

/**
 * Configuration for hotel trend (price changes)
 */
const HOTEL_TREND_STATES = {
  UP: 'up',
  DOWN: 'down',
  STABLE: 'stable'
} as const satisfies Record<string, HotelTrendDirection>

export const HOTEL_TREND_CONFIG = {
  states: HOTEL_TREND_STATES,

  iconByDirection: {
    up: 'hotelUp',
    down: 'hotelDown',
    stable: 'hotelStable'
  } as Record<HotelTrendDirection, PriceTrendIconKey>,

  titleMessageByDirection: {
    up: 'hotelChangePriceUp',
    down: 'hotelChangePriceDown',
    stable: 'hotelChangeNoChange'
  } as Record<HotelTrendDirection, PriceTrendMessageKey>,

  subtitleMessage: {
    messageKey: 'hotelChangeSubtitle' as const
  } as MessageConfig,

  tooltipMessage: {
    messageKey: 'hotelChangeTooltip' as const
  } as MessageConfig
} as const

/**
 * Configuration for similar hotels comparison
 */
const SIMILAR_HOTELS_STATES = {
  CHEAPER: 'cheaper',
  EXPENSIVE: 'expensive',
  SAME: 'same'
} as const satisfies Record<string, SimilarHotelsDirection>

export const SIMILAR_HOTELS_CONFIG = {
  states: SIMILAR_HOTELS_STATES,

  iconByDirection: {
    [SIMILAR_HOTELS_STATES.CHEAPER]: 'similarDown',
    [SIMILAR_HOTELS_STATES.EXPENSIVE]: 'similarUp',
    [SIMILAR_HOTELS_STATES.SAME]: 'similarDown' // Requirement: reuse down icon for the neutral state
  } as Record<SimilarHotelsDirection, PriceTrendIconKey>,

  titleMessageByDirection: {
    [SIMILAR_HOTELS_STATES.CHEAPER]: 'similarHotelsCheaper',
    [SIMILAR_HOTELS_STATES.EXPENSIVE]: 'similarHotelsMoreExpensive',
    [SIMILAR_HOTELS_STATES.SAME]: 'similarHotelsNoDifference'
  } as Record<SimilarHotelsDirection, PriceTrendMessageKey>,

  subtitleMessage: {
    messageKey: 'similarHotelsSubtitle' as const
  } as MessageConfig,

  tooltipMessage: {
    messageKey: 'similarHotelsDescription' as const
  } as MessageConfig
} as const

/**
 * Chart color palette for PriceHistoryChart
 *
 * Note: Canvas context requires actual hex color values, not CSS variables.
 * Theme tokens (cssTheme.colors) return CSS variable strings like 'var(--colors-brand-c600)'
 * which canvas cannot interpret. These hex values correspond to the theme tokens:
 *
 * - primary: #580AB8 (theme: colors.brand.c600)
 * - white: #FFFFFF (theme: colors.neutral.c000)
 * - text: #696663 (theme: colors.neutral.c600)
 * - background: #FAF8F4 (theme: colors.neutral.c050)
 * - gridLine: #DCD9D5 (theme: colors.neutral.c200)
 */
export const CHART_COLORS = {
  primary: '#580AB8', // Brand purple - main line color
  secondary: '#DCD9D5', // Gray for similar hotels line
  secondaryDot: '#9B908C', // Gray for similar hotels line dot
  verticalLine: '#9B9392', // Neutral gray - vertical reference line
  white: '#FFFFFF',
  text: '#615A56', // Neutral gray - axis labels
  background: '#FAF8F4', // Light beige - chart background
  gridLine: '#DCD9D5', // Light gray - grid lines
  labelText: {
    primary: '#ffffff', // White text on purple label
    secondary: '#111827' // Dark text on white label
  },
  subtitleText: '#180C06' // Dark text for x-axis subtitle and legend labels
} as const

/**
 * Layout constants for chart elements
 */
export const CHART_LAYOUT = {
  DOT_OFFSET_X: 4, // Offset dots left to prevent overlap with background edge
  DOT_RADIUS: 5, // Size of dots at line ends
  DOT_BORDER_WIDTH: 2,
  LABEL_HEIGHT: 20, // labelXS lineHeight (16px) + paddingY (2px * 2) = 20px
  LABEL_OFFSET_X: 15, // Space between label and dot
  VERTICAL_LINE_DAY_OFFSET: 7, // Which day to draw the vertical line at
  VERTICAL_LINE_DASH: [4, 4] as number[], // Dash pattern for vertical line
  X_AXIS_TOTAL_DAYS: 28, // Fixed number of days the X-axis always spans
  X_AXIS_TICK_INTERVAL: 7, // Interval between X-axis day labels
  X_AXIS_SUBTITLE_OFFSET_Y: 16, // Offset for "days ago" subtitle below the numbers
  X_AXIS_LABEL_OFFSET_Y: 16, // Space between chart and x-axis labels
  X_AXIS_TOTAL_HEIGHT: 28, // Total height reserved for custom x-axis (ticks + "days ago" subtitle)
  BACKGROUND_PADDING_Y: 10, // Top/bottom padding for rounded background
  BACKGROUND_RADIUS: 6, // Border radius for chart background (md = 6px)
  LABEL_SHADOW: {
    color: 'rgba(41, 39, 36, 0.12)', // #292724 at 12% opacity
    blur: 12,
    offsetX: 0,
    offsetY: 3
  }
} as const

/**
 * Label styling constants for price labels
 */
export const LABEL_STYLES = {
  paddingY: 2,
  paddingX: 4,
  borderRadius: CHART_LAYOUT.BACKGROUND_RADIUS,
  offsetX: CHART_LAYOUT.LABEL_OFFSET_X,
  primary: {
    background: CHART_COLORS.primary,
    text: CHART_COLORS.labelText.primary
  },
  secondary: {
    background: CHART_COLORS.white,
    text: CHART_COLORS.labelText.secondary
  },
  // Typography from theme
  ...cssTheme.typography.labelXS
} as const
